import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import UserModal from './UserModal';
import apiClient from '../../assets/scripts/services/apiClient';
import authService from '../../assets/scripts/auth/authService';
import './UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalTitle, setModalTitle] = useState('Create User');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    checkAuthAndLoadData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [users.length]);

  const checkAuthAndLoadData = async () => {
    const isAuthenticated = await authService.requireAuth();
    if (!isAuthenticated) {
      return;
    }
    await Promise.all([loadUsers(), loadOrganizations()]);
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get('/users');
      setUsers(data || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const data = await apiClient.get('/organizations');
      setOrganizations(data || []);
    } catch (err) {
      console.error('Error loading organizations:', err);
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalTitle('Create New User');
    setModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setModalTitle('Edit User');
    setModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await apiClient.delete(`/users/${userId}`);
      await loadUsers();
      showNotification('User deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting user:', err);
      showNotification('Failed to delete user', 'error');
    }
  };

  const handleSaveUser = async (formData) => {
    const detail = {};
    const userData = { detail };
    
    // Map form fields to detail object
    Object.keys(formData).forEach(key => {
      if (key === 'organization_id') {
        userData.organization_id = formData[key];
      } else if (key === 'password' && formData[key]) {
        userData.password = formData[key];
      } else if (formData[key] && formData[key].toString().trim()) {
        detail[key] = formData[key].toString().trim();
      }
    });

    try {
      if (selectedUser) {
        // Update existing user
        await apiClient.put(`/users/${selectedUser.id}`, userData);
        showNotification('User updated successfully', 'success');
      } else {
        // Create new user
        await apiClient.post('/users', userData);
        showNotification('User created successfully', 'success');
      }
      
      setModalOpen(false);
      await loadUsers();
    } catch (err) {
      console.error('Error saving user:', err);
      throw err; // Let modal handle the error
    }
  };

  const showNotification = (message, type = 'info') => {
    if (type === 'error') {
      alert('Error: ' + message);
    } else {
      alert('Success: ' + message);
    }
  };

  const handleViewUser = (user) => {
    console.log('View user:', user);
    window.location.href = `#user/${user.id}`;
  };

  const getUserTypeLabel = (userType) => {
    switch(userType) {
      case 'client_admin': return 'Client Admin';
      case 'client_user': return 'Client User';
      case 'super_user': return 'Super User';
      default: return userType || 'N/A';
    }
  };

  const getUserStatusBadge = (status) => {
    const isActive = status === 'active' || status === true;
    return (
      <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  const totalPages = Math.ceil(users.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedUsers = users.slice(startIndex, endIndex);

  return (
    <div className="user-management">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="peers ai-c jc-sb fxw-nw mB-30 mobile-header-stack">
              <div className="peer">
                <h4 className="c-grey-900 mT-10 mB-0">User Management</h4>
              </div>
              <div className="peer">
                <button 
                  className="btn btn-primary mobile-full-width" 
                  onClick={handleCreateUser}
                >
                  <i className="ti-plus mR-5"></i>Create New User
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="row">
          <div className="col-12">
            <div className="bgc-white bd bdrs-3 p-20 mB-20">
              <h5 className="c-grey-900 mB-20">Users</h5>
              <div className="table-responsive" style={{overflowX: 'auto'}}>
                <table className="table table-striped table-bordered" style={{width: '100%'}}>
                  <thead>
                    <tr>
                      <th>User Name</th>
                      <th>Full Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>User Type</th>
                      <th>Organization</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center">
                          No users found. Create your first user to get started.
                        </td>
                      </tr>
                    ) : (
                      paginatedUsers.map(user => {
                        const detail = user.detail || {};
                        const org = organizations.find(o => o.id === user.organization_id);
                        return (
                          <tr key={user.id}>
                            <td>{detail.user_name || 'N/A'}</td>
                            <td>{detail.user_full_name || 'N/A'}</td>
                            <td>{detail.email || 'N/A'}</td>
                            <td>{detail.phone_numebr || 'N/A'}</td>
                            <td>{getUserTypeLabel(detail.user_type)}</td>
                            <td>{org?.detail?.org_name || org?.detail?.name || 'N/A'}</td>
                            <td>
                              <button 
                                className="btn btn-sm btn-primary me-2"
                                onClick={() => handleViewUser(user)}
                              >
                                <i className="ti-eye"></i> View
                              </button>
                              <button 
                                className="btn btn-sm btn-secondary me-2"
                                onClick={() => handleEditUser(user)}
                              >
                                <i className="ti-pencil"></i> Edit
                              </button>
                              <button 
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                <i className="ti-trash"></i> Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="pagination-wrapper d-flex justify-content-center align-items-center mt-3">
                  <button 
                    className="btn btn-outline-secondary btn-sm me-2"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  
                  <span className="pagination-info mx-3">
                    Page {currentPage} of {totalPages} ({users.length} total users)
                  </span>
                  
                  <button 
                    className="btn btn-outline-secondary btn-sm ms-2"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <UserModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveUser}
        user={selectedUser}
        title={modalTitle}
        organizations={organizations}
      />
    </div>
  );
};

// Export component
export default UserManagement;

// Auto-mount if element exists
export const initUserManagement = () => {
  const mountElement = document.getElementById('user-management-root');
  if (mountElement) {
    const root = ReactDOM.createRoot(mountElement);
    root.render(<UserManagement />);
  }
};
